import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet, WalletMultiButton } from '../context/SimpleWalletContext';

interface Transaction {
    id: string;
    type: 'investment' | 'withdrawal' | 'project_creation';
    user: string;
    amount: number;
    project: string;
    timestamp: number;
    status: 'pending' | 'confirmed' | 'failed';
    signature: string;
}

const SolanaDemo: React.FC = () => {
    const { connected, publicKey, balance, updateBalance } = useWallet();
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [isSimulating, setIsSimulating] = useState(false);
    const [stats, setStats] = useState({
        totalProjects: 3,
        totalInvestments: 0,
        totalAmount: 0,
        activeUsers: 5
    });

    const generateTransaction = (type: Transaction['type'], user: string, amount: number, project: string): Transaction => ({
        id: `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type,
        user,
        amount,
        project,
        timestamp: Date.now(),
        status: 'pending',
        signature: `${Math.random().toString(36).substr(2, 9)}...${Math.random().toString(36).substr(2, 9)}`
    });

    const simulateTransaction = async (transaction: Transaction) => {
        setTransactions(prev => [transaction, ...prev]);

        // Simulate blockchain confirmation
        await new Promise(resolve => setTimeout(resolve, 2000));

        setTransactions(prev =>
            prev.map(tx =>
                tx.id === transaction.id
                    ? { ...tx, status: 'confirmed' as const }
                    : tx
            )
        );

        // Update stats
        if (transaction.type === 'investment') {
            setStats(prev => ({
                ...prev,
                totalInvestments: prev.totalInvestments + 1,
                totalAmount: prev.totalAmount + transaction.amount
            }));
        }
    };

    const runDemo = async () => {
        setIsSimulating(true);

        const demoTransactions = [
            generateTransaction('investment', 'Alice', 100, 'Solar Farm Karachi'),
            generateTransaction('investment', 'Bob', 50, 'Solar Farm Karachi'),
            generateTransaction('investment', 'Charlie', 75, 'Wind Energy Lahore'),
            generateTransaction('investment', 'Diana', 200, 'Solar Farm Karachi'),
            generateTransaction('investment', 'Eve', 150, 'Wind Energy Lahore'),
            generateTransaction('investment', 'Alice', 80, 'Hydroelectric Gilgit'),
            generateTransaction('withdrawal', 'Project Admin', 100, 'Solar Farm Karachi'),
        ];

        for (const tx of demoTransactions) {
            await simulateTransaction(tx);
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        setIsSimulating(false);
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">
                        🔗 Solana Blockchain Demo
                    </h1>
                    <p className="text-gray-600 mb-4">
                        Live simulation of Greenova8's Solana smart contract interactions
                    </p>
                    <div className="flex justify-center">
                        <WalletMultiButton className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105" />
                    </div>
                    {connected && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg"
                        >
                            <div className="text-center">
                                <p className="text-green-700 text-sm mb-2">
                                    ✅ Wallet Connected: {publicKey?.toBase58().slice(0, 8)}...{publicKey?.toBase58().slice(-8)}
                                </p>
                                <div className="text-green-600 font-bold text-xl">{balance} SOL</div>
                                <div className="text-gray-600 text-xs">Available Balance</div>
                            </div>
                        </motion.div>
                    )}
                </motion.div>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
                >
                    {[
                        { label: "Projects", value: stats.totalProjects, icon: "🏗️" },
                        { label: "Investments", value: stats.totalInvestments, icon: "💰" },
                        { label: "Total SOL", value: stats.totalAmount, icon: "📈" },
                        { label: "Users", value: stats.activeUsers, icon: "👥" }
                    ].map((stat, index) => (
                        <div key={index} className="bg-white rounded-xl p-4 shadow-lg border border-gray-200">
                            <div className="text-2xl mb-1">{stat.icon}</div>
                            <div className="text-xl font-bold text-gray-900">{stat.value}</div>
                            <div className="text-gray-600 text-sm">{stat.label}</div>
                        </div>
                    ))}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Transaction Feed */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white rounded-xl p-6 shadow-lg border border-gray-200"
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">📊 Live Transactions</h2>
                            <button
                                onClick={runDemo}
                                disabled={isSimulating}
                                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                            >
                                {isSimulating ? '🔄 Running...' : '▶️ Start Demo'}
                            </button>
                        </div>

                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            <AnimatePresence>
                                {transactions.map((tx) => (
                                    <motion.div
                                        key={tx.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        className={`p-4 rounded-lg border ${tx.status === 'confirmed'
                                            ? 'bg-green-50 border-green-200'
                                            : tx.status === 'pending'
                                                ? 'bg-yellow-50 border-yellow-200'
                                                : 'bg-red-50 border-red-200'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-gray-900 font-semibold">
                                                        {tx.type === 'investment' ? '💰' : '💸'} {tx.type.toUpperCase()}
                                                    </span>
                                                    <span className={`px-2 py-1 rounded text-xs ${tx.status === 'confirmed'
                                                        ? 'bg-green-500 text-white'
                                                        : tx.status === 'pending'
                                                            ? 'bg-yellow-500 text-white'
                                                            : 'bg-red-500 text-white'
                                                        }`}>
                                                        {tx.status}
                                                    </span>
                                                </div>
                                                <div className="text-gray-600 text-sm">
                                                    {tx.user} → {tx.project}
                                                </div>
                                                <div className="text-green-600 font-bold">
                                                    {tx.amount} SOL
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-gray-500 text-xs">
                                                    {new Date(tx.timestamp).toLocaleTimeString()}
                                                </div>
                                                <div className="text-gray-400 text-xs font-mono">
                                                    {tx.signature}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </motion.div>

                    {/* Smart Contract Info */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                        className="space-y-6"
                    >
                        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">🔧 Smart Contract</h2>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Program ID:</span>
                                    <span className="text-gray-900 font-mono text-sm">
                                        Fg6PaFpoGXkYsidMpWTK6W2BeZ7FEfcYkg476zPFsLnS
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Network:</span>
                                    <span className="text-green-600">Solana Devnet</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Framework:</span>
                                    <span className="text-gray-900">Anchor</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Status:</span>
                                    <span className="text-green-600">🟢 Active</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">⚡ Features</h2>
                            <div className="space-y-2">
                                {[
                                    '✅ Project Initialization',
                                    '✅ Investment Processing',
                                    '✅ Fund Withdrawal',
                                    '✅ Minimum Investment Validation',
                                    '✅ Target Amount Enforcement',
                                    '✅ Event Emission',
                                    '✅ PDA Management'
                                ].map((feature, index) => (
                                    <div key={index} className="text-gray-600 text-sm">
                                        {feature}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default SolanaDemo;


